import { useState } from "react";
import { adminApi } from "../lib/api";
import { Button, cx } from "../control/ui/primitives";
import { GoonlordsWordmark } from "../control/ui/Logo";
import { SystemSettings } from "./SystemSettings";
import { CollectionsAdmin } from "./CollectionsAdmin";
import { ContentReview } from "./ContentReview";
import { TagReports } from "./TagReports";
import { TagSuggestions } from "./TagSuggestions";
import { TagManager } from "./TagManager";
import { PositionReview } from "./PositionReview";
import { UsersAdmin } from "./UsersAdmin";
import { WallAdmin } from "./WallAdmin";
import { FapsAdmin } from "./FapsAdmin";
import { FeedAdmin } from "./FeedAdmin";

type SectionId = "review" | "tags" | "positions" | "collections" | "wall" | "feed" | "faps" | "system" | "users";

/** Nav sections. `soon` entries are seams reserved for later. */
const SECTIONS: ReadonlyArray<{ id: SectionId | string; label: string; soon?: boolean }> = [
  { id: "review", label: "Review" },
  { id: "tags", label: "Tags" },
  { id: "positions", label: "Positions" },
  { id: "collections", label: "Collections" },
  { id: "wall", label: "Wall" },
  { id: "feed", label: "Feed" },
  { id: "faps", label: "Faps" },
  { id: "system", label: "System" },
  { id: "users", label: "Users" },
  { id: "displays", label: "Displays", soon: true },
];

export function AdminPanel({ onLogout }: { onLogout: () => void }) {
  const [section, setSection] = useState<SectionId>("review");
  const [loggingOut, setLoggingOut] = useState(false);

  async function logout() {
    setLoggingOut(true);
    try {
      await adminApi.logout();
    } finally {
      onLogout();
    }
  }

  return (
    <div className="flex min-h-screen bg-[var(--color-ink)] text-zinc-200">
      {/* sidebar */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-[var(--color-edge)] bg-[var(--color-panel)] p-4">
        <div className="mb-6 flex items-center gap-2">
          <GoonlordsWordmark height={24} />
          <span className="text-sm font-semibold tracking-wide">Admin</span>
        </div>

        <nav className="flex flex-1 flex-col gap-1">
          {SECTIONS.map((s) =>
            s.soon ? (
              <div
                key={s.id}
                className="flex cursor-default items-center justify-between rounded-lg px-3 py-2 text-sm text-zinc-600"
              >
                <span>{s.label}</span>
                <span className="rounded bg-[var(--color-edge)] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-zinc-400">
                  soon
                </span>
              </div>
            ) : (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id as SectionId)}
                aria-current={s.id === section ? "page" : undefined}
                className={cx(
                  "flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  s.id === section
                    ? "bg-[var(--color-panel-2)] font-medium text-zinc-100"
                    : "text-zinc-300 hover:bg-[var(--color-panel-2)]/50",
                )}
              >
                <span>{s.label}</span>
              </button>
            ),
          )}
        </nav>

        <Button variant="ghost" className="mt-4 w-full" onClick={logout} disabled={loggingOut}>
          {loggingOut ? "Signing out…" : "Sign out"}
        </Button>
      </aside>

      {/* content */}
      <main className="flex-1 overflow-y-auto">
        <div
          className={cx(
            "mx-auto p-8",
            section === "system" ? "max-w-3xl" : "max-w-6xl",
          )}
        >
          {section === "review" && <ContentReview />}
          {section === "tags" && (
            <div className="space-y-10">
              <TagReports />
              <TagSuggestions />
              <TagManager />
            </div>
          )}
          {section === "positions" && <PositionReview />}
          {section === "system" && <SystemSettings />}
          {section === "collections" && <CollectionsAdmin />}
          {section === "wall" && <WallAdmin />}
          {section === "feed" && <FeedAdmin />}
          {section === "faps" && <FapsAdmin />}
          {section === "users" && <UsersAdmin />}
        </div>
      </main>
    </div>
  );
}
